// AI Gateway service - all calls go through Cloudflare AI Gateway via the proxy worker.
// The gateway handles provider routing (OpenAI, Anthropic, etc.) via dynamic routes.
// Just pass a model name or a gateway route name (e.g. "dynamic/resume") as the model.
import type { ExperienceWizardData, ExperienceEntryResult, AugmentationContext } from '../types/wizard';
import { jdKeywordsInSource } from '../utils/atsScore';
import { guidanceFor } from '../utils/fieldProfile';
import type { WordBanks } from '../context/ResumeContext';

// ---------------------------------------------------------------------------
// Gateway configuration
// ---------------------------------------------------------------------------
const PROXY_URL = 'https://proxy.draftresume.app/v1/chat/completions';
// Model selection. The `dynamic/resume-claude` gateway route named a retired
// model (claude-sonnet-4-20250514) as its primary, so every request silently
// fell through to its gpt-4o-mini fallback until that account ran out of
// credit and the route began returning 500s. Call the model directly instead;
// the env override lets benchmarks and staged rollouts compare candidates.
declare const process: { env?: Record<string, string | undefined> } | undefined;
const DEFAULT_MODEL =
  (typeof process !== 'undefined' && process?.env?.DR_TAILOR_MODEL) ||
  (import.meta as any).env?.VITE_TAILOR_MODEL ||
  'anthropic/claude-haiku-4-5';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------
interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  choices: Array<{
    message: {
      role?: string;
      content: string;
    };
    finish_reason?: string;
  }>;
  error?: {
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Core gateway call - pass a model or dynamic route name and go
// ---------------------------------------------------------------------------
// Per-attempt timeout. Measured 2026-08-22 against the live proxy: the full
// parallel retailor completes in 6.5-14s end to end for 2 to 10 job entries
// (24 runs), so user-facing copy says "usually under 30 seconds". The timeout
// stays generous because provider latency spikes and a 150s per-attempt cap
// with the 2-attempt retry bounds worst-case wall time at ~5 min, after which
// the deduct is refunded and the user is told to try again.
const CALL_TIMEOUT_MS = 150_000;

export async function callChat(
  messages: ChatMessage[],
  temperature: number = 0.3,
  model: string = DEFAULT_MODEL,
  signal?: AbortSignal,
  jsonMode: boolean = false
): Promise<ChatResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Pass user ID (not session token) for AI Gateway cost attribution
  const storedUser = localStorage.getItem('draftresume_auth_user');
  if (storedUser) {
    try { headers['x-user-id'] = JSON.parse(storedUser).id; } catch { /* skip */ }
  }

  // STREAMING: A non-streaming call for a 15-year resume's full retailor
  // (~6-8k output tokens, ~60-90s generation) consistently failed with
  // "AI Gateway returned an empty response" because the gateway's edge
  // timeout (~60s) cuts the upstream connection mid-generation and returns
  // an empty 200. Streaming keeps the connection alive via SSE chunks, so
  // generation can run as long as it needs. We assemble the full content
  // client-side and return it in the same shape as the non-streaming path
  // so callers don't see the difference.
  //
  // max_tokens explicit so the provider doesn't apply a tight default.
  // response_format json_object (when jsonMode) constrains the upstream
  // provider to emit valid JSON server-side. Long, complex prompts (e.g. the
  // 15-year resume retailor) otherwise return structurally broken JSON that
  // JSON.parse rejects mid-body.
  // 16384 leaves headroom for a 7+ entry resume with full bulletsMeta
  // (~12-14k tokens of JSON). 8192 truncated such resumes mid-experience
  // even though the model emitted complete-looking JSON via response_format.
  const bodyObj: Record<string, unknown> = { model, messages, temperature, max_tokens: 16384, stream: true };
  // Only OpenAI accepts response_format: json_object. Anthropic's API takes
  // json_schema and 400s on json_object, and a dynamic/* gateway route gives no
  // hint about which provider will serve it - the retired resume-claude route
  // only tolerated this because its OpenAI fallback was silently answering every
  // request. Send it where it is known to work, and rely on the prompts'
  // "return ONLY valid JSON" instruction everywhere else.
  const acceptsJsonObject = model.startsWith('openai/');
  if (jsonMode && acceptsJsonObject) bodyObj.response_format = { type: 'json_object' };
  const body = JSON.stringify(bodyObj);
  let lastFailure: 'empty' | 'other' = 'other';
  for (let attempt = 0; attempt < 2; attempt++) {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), CALL_TIMEOUT_MS);
    if (signal) {
      if (signal.aborted) timeoutController.abort();
      else signal.addEventListener('abort', () => timeoutController.abort(), { once: true });
    }

    let response: Response;
    try {
      response = await fetch(PROXY_URL, {
        method: 'POST',
        headers,
        body,
        signal: timeoutController.signal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && (err.name === 'AbortError' || /aborted/i.test(err.message))) {
        const abortErr = new Error('AI service is taking too long to respond. Your token has been refunded - try again in a moment.');
        abortErr.name = 'AbortError';
        throw abortErr;
      }
      throw err;
    }

    if (!response.ok) {
      // Read body for the error message; aborts on body read still need cleanup.
      const errText = await response.text().catch(() => '');
      clearTimeout(timeoutId);
      let parsedMsg: string | undefined;
      try { parsedMsg = JSON.parse(errText)?.error?.message; } catch { /* keep undefined */ }
      throw new Error(parsedMsg || `Gateway error (${response.status})`);
    }

    if (!response.body) {
      clearTimeout(timeoutId);
      console.error(`[callChat] no response body, status=${response.status}`);
      lastFailure = 'empty';
      if (attempt === 0) { await new Promise(r => setTimeout(r, 500)); continue; }
      throw new Error('AI service returned an empty response. Your token has been refunded - try again.');
    }

    try {
      const assembled = await assembleStream(response.body);
      clearTimeout(timeoutId);
      if (!assembled.content) {
        // Empty stream (no chunks delivered) - treat as transient and retry.
        lastFailure = 'empty';
        console.warn(`[callChat] empty stream (attempt ${attempt + 1}/2); retrying after 500ms`);
        if (attempt === 0) { await new Promise(r => setTimeout(r, 500)); continue; }
        throw new Error('AI service returned an empty response. Your token has been refunded - try again.');
      }
      return {
        choices: [{
          message: { role: 'assistant', content: assembled.content },
          finish_reason: assembled.finishReason ?? 'stop',
        }],
      } as ChatResponse;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && (err.name === 'AbortError' || /aborted/i.test(err.message))) {
        const abortErr = new Error('AI service is taking too long to respond. Your token has been refunded - try again in a moment.');
        abortErr.name = 'AbortError';
        throw abortErr;
      }
      throw err;
    }
  }
  throw new Error(lastFailure === 'empty'
    ? 'AI service returned an empty response. Your token has been refunded - try again.'
    : 'AI service unavailable. Please try again.');
}

// Read an OpenAI-compat SSE stream and concatenate `delta.content` into a
// single string. The upstream sends `data: {json}\n\n` per chunk, ending
// with `data: [DONE]`.
async function assembleStream(body: ReadableStream<Uint8Array>): Promise<{ content: string; finishReason?: string }> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let content = '';
  let finishReason: string | undefined;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const event = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = event.replace(/^data:\s*/, '').trim();
      if (!line || line === '[DONE]') continue;
      try {
        const parsed = JSON.parse(line);
        const delta = parsed?.choices?.[0]?.delta;
        if (typeof delta?.content === 'string') content += delta.content;
        const fr = parsed?.choices?.[0]?.finish_reason;
        if (fr) finishReason = fr;
      } catch {
        // Skip malformed chunks rather than fail the whole stream.
      }
    }
  }
  return { content, finishReason };
}

// Delegates to callChat so structured-output paths inherit the empty-200
// retry, timeout, and AbortError hardening. Strips optional ```json fences.
async function callChatJSON(messages: ChatMessage[], temperature = 0.3): Promise<string> {
  const data = await callChat(messages, temperature, DEFAULT_MODEL, undefined, true);
  let content = data.choices?.[0]?.message?.content || '';
  content = content.replace(/^```json?\n?/i, '').replace(/\n?```$/i, '').trim();
  if (!content) {
    throw new Error('AI service returned an empty response. Your token has been refunded - try again.');
  }
  return content;
}

import type { ResumeJSON } from '../types/resume';

// =============================================================================
// extractWordBanksAI - replaces the regex-based analyzeAll() word bank
// extractor. The old approach used a 6KB regex with thousands of `|`
// alternations that included nouns ("throughput", "events") in the action-
// verbs bucket, feeding garbage into the retailor prompt context. The LLM
// path returns clean, JD-specific buckets in one cheap call.
//
// Cached per JD-content hash in sessionStorage with a 24h TTL so repeated
// retailors against the same JD don't re-pay for extraction.
// =============================================================================

export interface WordBanksAI {
  actionVerbs: string[];
  skillsKeywords: string[];
  achievementPhrases: string[];
  industryTerms: string[];
}

const WORD_BANK_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const WORD_BANK_CACHE_PREFIX = 'dr_wb_';

async function hashJD(jd: string): Promise<string> {
  // Stable hash for cache keys. SubtleCrypto is universally available
  // in browsers; truncate to 16 hex chars for a short key.
  const data = new TextEncoder().encode(jd.trim().toLowerCase().slice(0, 8000));
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf).slice(0, 8))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function readWordBankCache(key: string): WordBanksAI | null {
  try {
    const raw = sessionStorage.getItem(WORD_BANK_CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; banks: WordBanksAI };
    if (Date.now() - parsed.ts > WORD_BANK_CACHE_TTL_MS) {
      sessionStorage.removeItem(WORD_BANK_CACHE_PREFIX + key);
      return null;
    }
    return parsed.banks;
  } catch {
    return null;
  }
}

function writeWordBankCache(key: string, banks: WordBanksAI): void {
  try {
    sessionStorage.setItem(WORD_BANK_CACHE_PREFIX + key, JSON.stringify({ ts: Date.now(), banks }));
  } catch { /* quota or unavailable; cache is best-effort */ }
}

/**
 * Extract word-bank context (action verbs, skills, achievement phrases,
 * industry terms) from a job description via the LLM. Cached per JD hash
 * for 24h. Returns empty buckets on failure so callers can degrade
 * gracefully (the retailor still works, just without per-JD vocabulary
 * boost in the prompt).
 */
export async function extractWordBanksAI(jobDescription: string): Promise<WordBanksAI> {
  const trimmed = jobDescription.trim();
  if (!trimmed) {
    return { actionVerbs: [], skillsKeywords: [], achievementPhrases: [], industryTerms: [] };
  }

  const key = await hashJD(trimmed);
  const cached = readWordBankCache(key);
  if (cached) return cached;

  const systemPrompt = `Extract resume-tailoring vocabulary from a job description. Return JSON ONLY, no markdown fences, no commentary.

Output shape:
{
  "actionVerbs": ["..."],            // 6-12 strong verbs the job description uses or invites (e.g. "led", "architected"). Lowercase, base form.
  "skillsKeywords": ["..."],         // 8-15 specific tools/tech/methodology terms from the job description (e.g. "Kubernetes", "Salesforce"). Preserve original casing.
  "achievementPhrases": ["..."],     // 4-8 outcome-shaped phrases (e.g. "reduced p95 by 40%", "drove $2M ARR"). May be empty if the job description lacks them.
  "industryTerms": ["..."]           // 4-10 domain terms a strong resume should signal back (e.g. "SRE", "B2B SaaS"). Lowercase.
}

Rules:
- Only emit terms that actually appear in or are clearly invited by the job description.
- Do NOT invent generic resume buzzwords ("synergy", "team-player").
- Do NOT include stop words or generic verbs ("work", "use", "do").
- If the job description is vague, return small buckets - empty buckets are acceptable.`;

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: `Job Description:\n${trimmed.slice(0, 8000)}` },
  ];

  try {
    const response = await callChat(messages, 0.2);
    const raw = response.choices?.[0]?.message?.content?.trim() ?? '';
    const stripped = raw.replace(/^```(?:json)?\n?/i, '').replace(/\n?```$/i, '').trim();
    const parsed = JSON.parse(stripped) as Partial<WordBanksAI>;
    const banks: WordBanksAI = {
      actionVerbs: Array.isArray(parsed.actionVerbs)
        ? parsed.actionVerbs.filter((s): s is string => typeof s === 'string').slice(0, 12)
        : [],
      skillsKeywords: Array.isArray(parsed.skillsKeywords)
        ? parsed.skillsKeywords.filter((s): s is string => typeof s === 'string').slice(0, 15)
        : [],
      achievementPhrases: Array.isArray(parsed.achievementPhrases)
        ? parsed.achievementPhrases.filter((s): s is string => typeof s === 'string').slice(0, 8)
        : [],
      industryTerms: Array.isArray(parsed.industryTerms)
        ? parsed.industryTerms.filter((s): s is string => typeof s === 'string').slice(0, 10)
        : [],
    };
    writeWordBankCache(key, banks);
    return banks;
  } catch (e) {
    console.warn('[extractWordBanksAI] failed; returning empty buckets:', e);
    return { actionVerbs: [], skillsKeywords: [], achievementPhrases: [], industryTerms: [] };
  }
}

// =============================================================================
// structureResumeViaAI - AI fallback for resumeParser.ts when the regex
// parser degrades to a single "Full Resume" section. Real users paste
// resumes with non-standard headers, paragraph blobs, or non-English
// content that the regex can't classify. The AI returns the same
// StructuredResume shape so callers don't change.
//
// This is the FALLBACK path. The regex parser runs first (cheaper, faster);
// if it produces multiple sections we trust it. We only pay for the AI
// call when the regex degraded to one big "Full Resume" blob.
// =============================================================================

interface AIStructuredSection {
  type: 'contact' | 'summary' | 'skills' | 'experience' | 'education' | 'certifications' | 'other';
  title: string;
  items: string[];
}

export interface AIStructuredResume {
  sections: AIStructuredSection[];
  originalText: string;
}

/**
 * Ask the LLM to break a resume into sections + items. Used as a fallback
 * when the regex parser produces only one big section (common for
 * paragraph-style resumes, Word-pasted formats, non-English content).
 *
 * Returns null on failure - caller falls back to whatever the regex parser
 * produced. Never throws.
 */
export async function structureResumeViaAI(rawText: string): Promise<AIStructuredResume | null> {
  const trimmed = rawText.trim();
  if (!trimmed) return null;

  const systemPrompt = `Break a resume into structured sections. Return JSON ONLY, no markdown fences, no commentary.

Output shape:
{
  "sections": [
    {
      "type": "contact" | "summary" | "skills" | "experience" | "education" | "certifications" | "other",
      "title": "User-facing label, e.g. 'Experience'",
      "items": ["one item per line - bullet, job-title line, skill, etc."]
    }
  ]
}

Rules:
- Always include a "contact" section first if there's a name/email/phone block at the top.
- Each "item" in items[] is a SINGLE line of the source resume (job title lines, bullets, skill list entries, etc.). Strip leading "-" / "*" / "•" but preserve the rest.
- Group bullets with their parent job title in the same "experience" section.
- For "skills", split the original list into individual skill items (one per skill).
- For "education", one item per degree row.
- DO NOT rewrite, paraphrase, or augment content. Just structure what's there.
- Keep ALL of the source text - nothing should be dropped.`;

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: `Resume:\n${trimmed.slice(0, 16000)}` },
  ];

  try {
    const response = await callChat(messages, 0.1);
    const raw = response.choices?.[0]?.message?.content?.trim() ?? '';
    const stripped = raw.replace(/^```(?:json)?\n?/i, '').replace(/\n?```$/i, '').trim();
    const parsed = JSON.parse(stripped) as { sections?: unknown };
    if (!Array.isArray(parsed.sections)) return null;

    const validTypes = new Set(['contact', 'summary', 'skills', 'experience', 'education', 'certifications', 'other']);
    const sections: AIStructuredSection[] = [];
    for (const sec of parsed.sections) {
      if (!sec || typeof sec !== 'object') continue;
      const s = sec as Record<string, unknown>;
      if (typeof s.type !== 'string' || !validTypes.has(s.type)) continue;
      if (typeof s.title !== 'string') continue;
      if (!Array.isArray(s.items)) continue;
      const items = s.items.filter((i): i is string => typeof i === 'string' && i.trim().length > 0);
      if (items.length === 0) continue;
      sections.push({
        type: s.type as AIStructuredSection['type'],
        title: s.title.slice(0, 80),
        items,
      });
    }
    if (sections.length === 0) return null;
    return { sections, originalText: rawText };
  } catch (e) {
    console.warn('[structureResumeViaAI] failed; falling back to regex parser:', e);
    return null;
  }
}

/**
 * Generate a targeted cover letter based on the candidate's resume and a job description.
 * Output is plain-text, ready to paste into a form or export to PDF/Word.
 *
 * Tone options shape the voice but not the factual content - the model should
 * never invent experience the resume doesn't already claim.
 */
export async function generateCoverLetter(
  resume: ResumeJSON,
  jobDescription: string,
  options?: { tone?: 'professional' | 'enthusiastic' | 'concise' }
): Promise<string> {
  const tone = options?.tone ?? 'professional';
  const toneGuidance = {
    professional: 'Measured, confident, and polished. Avoid hype and cliché.',
    enthusiastic: 'Warm and personal. Convey genuine interest in the role without gushing.',
    concise: 'Short and direct. Cut every adjective that doesn\'t earn its place. Target ~200 words.',
  }[tone];

  const systemPrompt = `You are an expert cover letter writer. Write a one-page cover letter for the candidate based on their resume and the job description they're applying to.

Rules:
- Do NOT invent experience, skills, or accomplishments that aren't in the resume.
- Do reference specific, concrete achievements from the resume that match the job.
- Pull 2-3 keywords from the job description and weave them in naturally.
- Address the hiring manager generically ("Dear Hiring Manager,") unless the job description names one.
- Open with why the candidate is a fit, not "I am applying for...".
- Close with a clear ask for next steps.
- Output plain text only. No markdown, no code fences. Use single blank lines between paragraphs.
- 3-4 paragraphs total. Under 350 words.

Tone: ${toneGuidance}`;

  const userPrompt = `Candidate resume (JSON):\n${JSON.stringify(resume, null, 2)}\n\nJob description:\n${jobDescription}\n\nWrite the cover letter now.`;

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ];

  const response = await callChat(messages, 0.4);
  let text = response.choices[0].message.content;
  // Strip any accidental markdown fences the model wrapped around the output
  text = text.replace(/^```[a-z]*\n?/i, '').replace(/\n?```$/i, '').trim();
  return text;
}

/**
 * Generate a custom HTML resume template based on a vibe description.
 * Returns self-contained HTML with the resume data filled in.
 */
export async function generateVibeTemplate(vibe: string, resume: ResumeJSON): Promise<string> {
  const messages: ChatMessage[] = [
    {
      role: 'system',
      content: `You are a world-class web designer. Generate a complete, self-contained HTML document with embedded CSS that renders as a professional one-page resume.

The design vibe the user wants: "${vibe}"

Requirements:
- Complete <!DOCTYPE html> document with embedded <style> block
- Visually distinctive and creative while remaining professional and readable
- A4/Letter sized (max-width: 8.5in, proper margins)
- Include all resume sections: name, title, contact, summary, experience, skills, education
- Use the exact data provided - do not change any content
- Print-friendly (include @media print styles)
- No external dependencies (no CDN links, no JavaScript)

Return ONLY the complete HTML document. No explanation, no code fences.`
    },
    {
      role: 'user',
      content: `Resume data:\n${JSON.stringify(resume, null, 2)}`
    }
  ];

  const response = await callChat(messages, 0.7); // Higher temperature for creativity
  let html = response.choices[0].message.content;
  // Strip markdown code fences if the AI wrapped it
  html = html.replace(/^```html?\n?/i, '').replace(/\n?```$/i, '').trim();
  return html;
}

// Skeleton entry: just the header. Per-entry calls extract their own source
// bullets from the full resume (avoids re-emitting them in skeleton output,
// which was a 20+ second cost on a 7-job resume).
interface SkeletonExperienceEntry {
  title: string;
  company: string;
  dates: string;
  location?: string;
}

const SKELETON_SCHEMA = `{
  "name": "Full Name",
  "title": "Professional Title",
  "contact": { "email": "...", "phone": "...", "location": "City, ST", "linkedin": "..." },
  "summary": "2-3 sentence professional summary tailored to the job description",
  "summaryMeta": {
    "originalText": "the closest matching summary/objective from the source resume, or null if none",
    "rationale": "one sentence citing a specific requirement from the job description this summary now surfaces",
    "keywords": ["keyword1", "keyword2"]
  },
  "skills": ["Skill1", "Skill2", ...],
  "skillsAddedFromJD": ["skills surfaced from the job description but not in the original resume's skills list"],
  "experience": [
    { "title": "Job Title", "company": "Company", "dates": "Start - End", "location": "City, ST" }
  ],
  "education": [
    { "degree": "Degree", "school": "School Name", "year": "Year" }
  ],
  "certifications": ["Cert 1"],
  "additionalSections": [
    { "heading": "Projects", "items": ["one line per item, copied verbatim from the source"] }
  ]
}`;

const ENTRY_SCHEMA = `{
  "bullets": ["Achievement 1 (rewritten for the job description)", "Achievement 2", ...]
}`;

const META_SCHEMA = `{
  "bulletsMeta": [
    {
      "originalText": "VERBATIM copy of the source-resume bullet this rewrite is based on - copy the words exactly as they appear in the source, do not paraphrase or summarize. null ONLY if this bullet has no basis in the source at all",
      "changeType": "unchanged | reworded | strengthened | added",
      "rationale": "ONE short sentence (max ~15 words) citing a specific requirement or keyword from the job description",
      "keywords": ["keyword-from-job-description"]
    }
  ]
}`;

function buildSharedContext(wordBanks?: Partial<WordBanks>): string {
  if (!wordBanks) return '';
  let ctx = '';
  if (wordBanks.actionVerbs?.length) ctx += `Action Verbs: ${wordBanks.actionVerbs.join(', ')}\n`;
  if (wordBanks.skillsKeywords?.length) ctx += `Skills & Keywords: ${wordBanks.skillsKeywords.join(', ')}\n`;
  if (wordBanks.achievementPhrases?.length) ctx += `Achievement Phrases: ${wordBanks.achievementPhrases.join(', ')}\n`;
  if (wordBanks.industryTerms?.length) ctx += `Industry Terms: ${wordBanks.industryTerms.join(', ')}\n`;
  return ctx;
}

// Pass 1: structure the source resume + tailor the JD-sensitive lightweight
// fields (summary, skills) in a single fast call. Experience entries come
// back as headers + verbatim source bullets - no rewriting yet.
/**
 * A rewrite instruction that names the job-posting words the candidate's own
 * resume already uses, and forbids losing them.
 *
 * This is the fix for a measured defect: the tailor compressed and rephrased
 * bullets and, in doing so, dropped posting keywords the source genuinely
 * contained, so some resumes came out matching the job less than they went in.
 * The rule is conditional - keep the word only where a source line already has
 * it - so it can never push an unrelated term into a bullet, and it is capped
 * so the prompt stays a rewrite instruction rather than a word list.
 *
 * Returns '' when nothing overlaps, so the prompt is unchanged for resumes that
 * do not need it.
 */
function preserveKeywordsClause(jobDescription: string, currentResume: string): string {
  const keep = jdKeywordsInSource(jobDescription, currentResume).slice(0, 40);
  if (!keep.length) return '';
  return `- KEEP THE CANDIDATE'S OWN KEYWORDS. These job-posting terms already appear in the source resume, so they are the candidate's real words, not stuffing: ${keep.join(', ')}. Wherever one of these appears in a source line you rewrite, it MUST still appear in your rewrite, in the same form - do not drop it, swap it for a synonym, or change its ending (keep "manager", do not write "management"). Never add one to a line whose source does not already support it.`;
}

async function generateResumeSkeleton(
  jobDescription: string,
  currentResume: string,
  wordBanks?: Partial<WordBanks>,
): Promise<{
  name: string;
  title: string;
  contact: Record<string, string>;
  summary: string;
  summaryMeta?: import('../types/resume').ProseMetadata | null;
  skills: string[];
  skillsAddedFromJD?: string[];
  experience: SkeletonExperienceEntry[];
  education: Array<{ degree: string; school: string; year: string }>;
  certifications?: string[];
  additionalSections?: Array<{ heading: string; items: string[] }>;
}> {
  const sharedContext = buildSharedContext(wordBanks);
  // Silent field awareness: shape the tailoring to the candidate's actual
  // field, detected from their resume and the posting. See utils/fieldProfile.
  const fieldBlock = `Field-specific guidance for this candidate (follow it):
${guidanceFor(currentResume, jobDescription)}`;
  const systemPrompt = `You are an expert resume writer. Structure the source resume into JSON and tailor the lightweight fields (summary, skills) for the job description. DO NOT rewrite or even echo the experience bullets - list only the experience headers (title, company, dates, location). A separate pass rewrites bullets per job.

${sharedContext ? `Use these resources where appropriate:\n${sharedContext}\n` : ''}

Rules:
- NEVER INVENT. Every employer, title, date, degree, certification and number in your output must appear in the source resume. The candidate will send this to employers under their own name; anything you add that they did not do is a lie they have to defend in an interview.
- The job description tells you what to EMPHASIZE, never what to CLAIM. Do not copy figures from it (team sizes, budgets, headcounts, years of experience, percentages). If the source does not state a number, the output has no number.
- "title" is ONE title, copied verbatim from the source resume - the most recent or most relevant one. Never replace it with the job description's title, and never staple two titles together ("Branch Manager Operations Manager" is a fabrication, not a tailored title). A Chef applying to an Operations Manager role is still a Chef.
- Do not total up years of experience. "7+ years in operations", "over a decade in logistics" and similar are claims the source must state in those words; do not compute them from date ranges or infer them from a career arc.
- Do not characterize employers or scope beyond what the source says: no "large organization", "enterprise", "high-volume", "Fortune 500", or team/revenue sizes the source never gives.
- The summary may only assert experience the source supports. Reframing "managed kitchen staff" as "team leadership" is fine; asserting "P&L ownership" the source never mentions is not.
- "skills" may contain ONLY skills the source resume shows. Skills from the job description that the source does not support go in "skillsAddedFromJD" and nowhere else - that field is surfaced to the user as a suggestion to confirm, so putting an unsupported skill in "skills" puts a false claim in their resume.
- CRITICAL: Include EVERY experience entry from the source resume - never drop, merge, or omit an employer.
- CRITICAL: The skills array MUST preserve EVERY specific technology, programming language, framework, cloud platform, and tool from the source resume's skills section AND experience bullets. Use specific names ("Terraform", "Java", "Kafka", "C#"), NOT categories like "cloud platforms" or "technical leadership". You may add skills from the job description on top.
- experience[i] contains ONLY the header: title, company, dates, location. NO bullets. Per-entry rewriting happens in a separate call.
- Tailor the summary (2-3 sentences) and the skills selection to the job description.
${fieldBlock}
- Capture EVERY section that is not summary, skills, experience, education or certifications into additionalSections - Projects, Publications, Volunteer, Awards, Honors, Languages, Licenses, Affiliations, Training, and any other. One entry per section, its real heading, one item per line COPIED VERBATIM from the source. Do NOT rewrite, tailor, summarize or invent these - they are facts (a license number, a paper title) and changing them fabricates. Never drop a section: content the candidate wrote that lands nowhere is content we deleted from their resume. Omit additionalSections only when the resume genuinely has no such section.
${preserveKeywordsClause(jobDescription, currentResume)}
- summaryMeta cites a specific requirement from the job description; rationale must reference the job description, not be generic.
- skillsAddedFromJD lists skills surfaced from the job description that aren't in the source resume.

Return a JSON object with this EXACT structure:
${SKELETON_SCHEMA}

IMPORTANT: Return ONLY valid JSON. No markdown, no fences, no commentary.`;

  const userPrompt = `Job Description:\n${jobDescription}\n\nCurrent Resume:\n${currentResume}`;

  const raw = await callChatJSON([
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ], 0.3);

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const pos = parseInt(msg.match(/position\s+(\d+)/i)?.[1] ?? '-1', 10);
    const ctx = pos >= 0 ? JSON.stringify(raw.substring(Math.max(0, pos - 150), pos + 150)) : '(no position)';
    console.error('[generateResumeSkeleton] JSON.parse failed:', msg, '| raw.length=', raw.length, '| ctx:', ctx);
    // Include forensic detail in the thrown message so the WAE metric blob
    // (capped at 300 chars server-side) captures enough to tell apart
    // truncation-at-max-tokens (raw ends mid-string) vs a stray comma or
    // unescaped char somewhere in the middle. Without this, the daily
    // report just says "AI returned invalid JSON" with no actionable
    // detail.
    const tail = raw.slice(-80).replace(/\s+/g, ' ');
    throw new Error(`AI returned invalid JSON (len=${raw.length}, parse:${msg.slice(0, 60)}, tail:${tail})`);
  }

  if (typeof parsed.name !== 'string' || !parsed.name.trim()) {
    throw new Error('AI response is missing a name. Please try again.');
  }
  if (!Array.isArray(parsed.experience)) {
    throw new Error('AI response has an invalid experience section. Please try again.');
  }
  if (!Array.isArray(parsed.skills)) {
    throw new Error('AI response has an invalid skills section. Please try again.');
  }

  return {
    name: parsed.name,
    title: typeof parsed.title === 'string' ? parsed.title : '',
    contact: (parsed.contact && typeof parsed.contact === 'object') ? parsed.contact as Record<string, string> : {},
    summary: typeof parsed.summary === 'string' ? parsed.summary : '',
    summaryMeta: normalizeProseMeta(parsed.summaryMeta),
    skills: parsed.skills.filter((s): s is string => typeof s === 'string'),
    skillsAddedFromJD: Array.isArray(parsed.skillsAddedFromJD)
      ? (parsed.skillsAddedFromJD as unknown[]).filter((s): s is string => typeof s === 'string')
      : undefined,
    experience: (parsed.experience as unknown[])
      .filter(e => e && typeof e === 'object')
      .map(e => {
        const r = e as Record<string, unknown>;
        return {
          title: typeof r.title === 'string' ? r.title : '',
          company: typeof r.company === 'string' ? r.company : '',
          dates: typeof r.dates === 'string' ? r.dates : '',
          location: typeof r.location === 'string' ? r.location : undefined,
        };
      }),
    education: Array.isArray(parsed.education)
      ? (parsed.education as unknown[])
          .filter(e => e && typeof e === 'object')
          .map(e => {
            const r = e as Record<string, unknown>;
            return {
              degree: typeof r.degree === 'string' ? r.degree : '',
              school: typeof r.school === 'string' ? r.school : '',
              year: typeof r.year === 'string' ? r.year : '',
            };
          })
      : [],
    certifications: Array.isArray(parsed.certifications)
      ? (parsed.certifications as unknown[]).filter((c): c is string => typeof c === 'string')
      : undefined,
    additionalSections: Array.isArray(parsed.additionalSections)
      ? (parsed.additionalSections as unknown[])
          .filter((x) => x && typeof x === 'object')
          .map((x) => {
            const r = x as Record<string, unknown>;
            return {
              heading: typeof r.heading === 'string' ? r.heading.slice(0, 80) : '',
              items: Array.isArray(r.items)
                ? (r.items as unknown[]).filter((i): i is string => typeof i === 'string' && i.trim().length > 0)
                : [],
            };
          })
          // A heading with no items, or items with no heading, is not a section.
          .filter((sec) => sec.heading && sec.items.length > 0)
      : undefined,
  };
}

// Pass 2 (one of N parallel): rewrite a single experience entry's bullets
// against the JD. The full source resume is included in the prompt so this
// call can locate the right job's bullets itself - that lets the skeleton
// stay light (no source-bullet echoing). N of these run via Promise.all so
// wall time = max(per-entry) instead of sum.
async function retailorExperienceEntry(
  jobDescription: string,
  currentResume: string,
  entry: SkeletonExperienceEntry,
  wordBanks?: Partial<WordBanks>,
): Promise<{ bullets: string[]; bulletsMeta?: import('../types/resume').BulletMetadata[]; failed?: boolean }> {
  const sharedContext = buildSharedContext(wordBanks);
  // Silent field awareness: shape the tailoring to the candidate's actual
  // field, detected from their resume and the posting. See utils/fieldProfile.
  const fieldBlock = `Field-specific guidance for this candidate (follow it):
${guidanceFor(currentResume, jobDescription)}`;
  const systemPrompt = `You are an expert resume writer. Rewrite the bullets for ONE specific job in the candidate's resume to align with the job description. Output strict JSON only.

${sharedContext ? `Use these resources where appropriate:\n${sharedContext}\n` : ''}

Rules:
- Locate the bullets for the specified job (by company + title + dates) in the source resume.
- Output one rewritten bullet per source bullet for that job. Same count, same order. NEVER drop a source bullet.
- NEVER INVENT. Rewrite what the source bullet says; do not add facts it does not contain. The candidate has to defend every line of this in an interview.
- Numbers are sacred. Carry over the source's own metrics; never invent one, never sharpen a vague claim into a precise one, and never copy a figure out of the job description (a posting asking for "a team of 6" does not mean this candidate led 6). A source bullet with no number produces a rewritten bullet with no number.
- Do not add responsibilities, tools, systems, certifications or scope the source bullet does not show. Reframing the same work in the job's vocabulary is the goal; adding new work is not.
- Surface keywords from the job description naturally where appropriate; do not stuff.
${fieldBlock}
${preserveKeywordsClause(jobDescription, currentResume)}
- Every rewritten bullet must be traceable to one specific source bullet. It is checked automatically: the source text you cite has to appear in the source resume, so cite it verbatim rather than describing it.

Return JSON with this EXACT shape:
${ENTRY_SCHEMA}

IMPORTANT: Return ONLY valid JSON. No markdown, no fences, no commentary.`;

  const userPrompt = `Job Description:\n${jobDescription}\n\nFull Source Resume:\n${currentResume}\n\nRewrite the bullets for the candidate's role at ${entry.company} as ${entry.title} (${entry.dates}). Find that job in the source resume above and return rewritten bullets + bulletsMeta for it.`;

  const raw = await callChatJSON([
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ], 0.3);

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error(`[retailorExperienceEntry] JSON.parse failed for ${entry.company}:`, msg, '| raw.length=', raw.length);
    return { bullets: [], bulletsMeta: undefined, failed: true };
  }

  // A missing or non-array bullets field means the model ignored the schema.
  // Every real job in the source resume has bullets, so this is a failed
  // rewrite, not a valid empty result. Flag it so the orchestrator can retry
  // and, failing that, refund - rather than handing back a job with no
  // bullets that the user already paid a full token for. A clean, genuinely
  // empty array (rare) is left as-is and not treated as a failure.
  if (!Array.isArray(parsed.bullets)) {
    console.error(`[retailorExperienceEntry] missing/invalid bullets array for ${entry.company}`);
    return { bullets: [], bulletsMeta: undefined, failed: true };
  }

  const bullets = (parsed.bullets as unknown[]).filter((b): b is string => typeof b === 'string');
  const bulletsMeta = normalizeBulletsMeta(parsed.bulletsMeta, bullets.length);
  return { bullets, bulletsMeta };
}

// Tag pass: takes the rewritten bullets + their source resume context and
// produces parallel bulletsMeta. Tiny input/output → fast (~2-4s per call,
// fans out N in parallel for ~3-5s wall). Runs AFTER the bullet rewrite
// because it needs the rewritten text to annotate.
async function tagBulletsForEntry(
  jobDescription: string,
  currentResume: string,
  entry: SkeletonExperienceEntry,
  rewrittenBullets: string[],
): Promise<import('../types/resume').BulletMetadata[] | undefined> {
  if (rewrittenBullets.length === 0) return undefined;

  const systemPrompt = `You annotate resume bullets with diff/insight metadata. Output strict JSON only.

Rules:
- bulletsMeta is parallel to the rewritten bullets list (index i annotates bullets[i]).
- originalText: the closest matching bullet from the source resume under the SAME job. Null if net-new.
- changeType:
  - "unchanged" only if trivially identical after trimming whitespace.
  - "reworded" for light phrasing edits with no material change.
  - "strengthened" when quantification, sharper verb, or a keyword from the job description was added.
  - "added" when originalText is null.
- rationale: ONE short sentence (max ~15 words) citing a specific requirement or keyword from the job description. Forbidden: "improves clarity", "sounds more professional", "better word choice".
- keywords: only terms from the job description that are actually present in the rewritten bullet.

Return JSON with this EXACT shape:
${META_SCHEMA}

IMPORTANT: Return ONLY valid JSON. No markdown, no fences, no commentary.`;

  const userPrompt = `Job Description:\n${jobDescription}\n\nFull Source Resume:\n${currentResume}\n\nThe candidate's role at ${entry.company} as ${entry.title} (${entry.dates}) was rewritten to these bullets:\n${rewrittenBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')}\n\nProduce bulletsMeta annotating each rewritten bullet against its source counterpart in the resume above.`;

  const raw = await callChatJSON([
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ], 0.2);

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error(`[tagBulletsForEntry] JSON.parse failed for ${entry.company}:`, msg);
    return undefined;
  }
  return normalizeBulletsMeta(parsed.bulletsMeta, rewrittenBullets.length);
}

/**
 * Generate a structured JSON resume tailored to the job description.
 *
 * Three-pass design (replaces a single ~50s serial call):
 *   1. Skeleton call (~15s): name/contact/summary+meta/skills+jdSkills/edu/certs
 *      + experience headers ONLY (no bullets, no source-bullet echo).
 *   2. Per-entry bullet calls (parallel, ~8s wall): each rewrites one job's
 *      bullets against the JD. No metadata in this call - keeps output
 *      tokens small so wall time = max(per-entry) instead of dominating.
 *   3. Per-entry tag calls (parallel, ~5s wall): each produces bulletsMeta
 *      (originalText, changeType, rationale, keywords) for its job. Tiny
 *      input/output. Failures here degrade the diff panel but don't break
 *      the resume.
 *
 * Net: a 7-employer resume that took ~50s now takes ~28s (~1.8x faster)
 * with the diff/insight feature intact.
 */
export async function generateStructuredResume(
  jobDescription: string,
  currentResume: string,
  wordBanks?: Partial<WordBanks>,
  onProgress?: (message: string) => void
): Promise<ResumeJSON> {
  onProgress?.('Structuring your resume...');
  const skeleton = await generateResumeSkeleton(jobDescription, currentResume, wordBanks);

  const n = skeleton.experience.length;
  onProgress?.(n > 0 ? `Tailoring ${n} job${n === 1 ? '' : 's'} in parallel...` : 'Finalizing...');

  const runEntry = (e: SkeletonExperienceEntry) =>
    retailorExperienceEntry(jobDescription, currentResume, e, wordBanks)
      .catch(err => {
        console.error(`[generateStructuredResume] entry retailor failed for ${e.company}:`, err);
        return { bullets: [], bulletsMeta: undefined, failed: true };
      });

  const entryResults = await Promise.all(skeleton.experience.map(runEntry));

  // Retry only the jobs whose rewrite failed (parse error or schema miss).
  // LLM JSON failures are frequently transient, and a partial failure here
  // used to be silently swallowed into a zero-bullet job on a resume the
  // user already paid a full token for. Retrying just the failed entries
  // keeps the extra cost proportional to the failure.
  const failedIdx = entryResults.flatMap((r, i) => (r.failed ? [i] : []));
  if (failedIdx.length > 0) {
    onProgress?.(`Retrying ${failedIdx.length} job${failedIdx.length === 1 ? '' : 's'}...`);
    const retried = await Promise.all(failedIdx.map(i => runEntry(skeleton.experience[i])));
    failedIdx.forEach((idx, k) => { entryResults[idx] = retried[k]; });
  }

  // If any job still has no rewritten bullets after the retry, the rewrite
  // genuinely failed. Don't hand back a silently-degraded resume - throw so
  // BuilderWizard's catch refunds the deduct (same contract as the empty-
  // response path), rather than charging a full token for a broken result.
  if (entryResults.some(r => r.failed)) {
    throw new Error('AI service could not rewrite every job. Your token has been refunded - try again.');
  }

  // Tag pass: parallel metadata generation. Failures here only blank the
  // diff/insight panel for that entry - the resume itself is still valid.
  onProgress?.('Annotating changes...');
  const metaResults = await Promise.all(
    skeleton.experience.map((e, i) =>
      tagBulletsForEntry(jobDescription, currentResume, e, entryResults[i].bullets)
        .catch(err => {
          console.error(`[generateStructuredResume] tag pass failed for ${e.company}:`, err);
          return undefined;
        })
    )
  );

  onProgress?.('Assembling final resume...');
  return {
    name: skeleton.name,
    title: skeleton.title,
    contact: skeleton.contact,
    summary: skeleton.summary,
    summaryMeta: skeleton.summaryMeta ?? undefined,
    skills: skeleton.skills,
    skillsAddedFromJD: skeleton.skillsAddedFromJD,
    experience: skeleton.experience.map((e, i) => ({
      title: e.title,
      company: e.company,
      dates: e.dates,
      location: e.location,
      bullets: entryResults[i].bullets,
      bulletsMeta: metaResults[i],
    })),
    education: skeleton.education,
    certifications: skeleton.certifications,
    additionalSections: skeleton.additionalSections,
  };
}

// Validate and coerce a single BulletMetadata. Returns null if the shape is
// unusable, letting the caller drop the whole bulletsMeta array rather than
// surfacing a half-broken annotation.
function normalizeBulletMeta(raw: unknown): import('../types/resume').BulletMetadata | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const changeType = m.changeType;
  const valid: import('../types/resume').ChangeType[] = ['unchanged', 'reworded', 'strengthened', 'added'];
  if (typeof changeType !== 'string' || !valid.includes(changeType as import('../types/resume').ChangeType)) return null;
  return {
    originalText: typeof m.originalText === 'string' ? m.originalText : null,
    changeType: changeType as import('../types/resume').ChangeType,
    rationale: typeof m.rationale === 'string' ? m.rationale : '',
    keywords: Array.isArray(m.keywords) ? m.keywords.filter((k): k is string => typeof k === 'string') : [],
  };
}

// Meta array must match bullets length exactly - a parallel array with the
// wrong length can't be trusted to annotate the right bullet. Drop the whole
// array rather than silently misalign.
function normalizeBulletsMeta(
  raw: unknown,
  expectedLength: number
): import('../types/resume').BulletMetadata[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  if (raw.length !== expectedLength) {
    console.warn(`[generateStructuredResume] bulletsMeta length (${raw.length}) != bullets length (${expectedLength}); dropping meta for this entry.`);
    return undefined;
  }
  const normalized = raw.map(normalizeBulletMeta);
  if (normalized.some(m => m === null)) {
    console.warn('[generateStructuredResume] bulletsMeta had malformed entries; dropping meta for this entry.');
    return undefined;
  }
  return normalized as import('../types/resume').BulletMetadata[];
}

function normalizeProseMeta(raw: unknown): import('../types/resume').ProseMetadata | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const m = raw as Record<string, unknown>;
  return {
    originalText: typeof m.originalText === 'string' ? m.originalText : null,
    rationale: typeof m.rationale === 'string' ? m.rationale : '',
    keywords: Array.isArray(m.keywords) ? m.keywords.filter((k): k is string => typeof k === 'string') : [],
  };
}

/**
 * Generate tailored resume content using AI
 * @param jobDescription - Target job description
 * @param currentResume - Current resume content
 * @param wordBanks - Optional word banks for context
 * @param onProgress - Optional callback for progress updates
 * @returns Generated resume content
 */
export async function generateTailoredResume(
  jobDescription: string,
  currentResume: string,
  wordBanks?: Partial<WordBanks>,
  onProgress?: (message: string) => void
): Promise<string> {
  onProgress?.('Building context from word banks...');

  // Build context from word banks (matching vanilla JS names)
  let sharedContext = '';
  if (wordBanks) {
    if (wordBanks.actionVerbs && wordBanks.actionVerbs.length > 0) {
      sharedContext += `Action Verbs: ${wordBanks.actionVerbs.join(', ')}\n`;
    }
    if (wordBanks.skillsKeywords && wordBanks.skillsKeywords.length > 0) {
      sharedContext += `Skills & Keywords: ${wordBanks.skillsKeywords.join(', ')}\n`;
    }
    if (wordBanks.achievementPhrases && wordBanks.achievementPhrases.length > 0) {
      sharedContext += `Achievement Phrases: ${wordBanks.achievementPhrases.join(', ')}\n`;
    }
    if (wordBanks.industryTerms && wordBanks.industryTerms.length > 0) {
      sharedContext += `Industry Terms: ${wordBanks.industryTerms.join(', ')}\n`;
    }
  }

  const systemPrompt = `You are an expert resume writer. Tailor the resume to match the job description while maintaining truthfulness.

${sharedContext ? `Use these resources where appropriate:\n${sharedContext}\n` : ''}

Instructions:
- Highlight relevant skills and experiences
- Use keywords from the job description naturally
- Maintain the original experience and qualifications
- Make it ATS-friendly

Format the output as CommonMark markdown:
- Use # for the person's name, ## for section headers (## Experience, ## Skills, etc.)
- Use **bold** for job titles and company names
- Use bullet lists (- ) for achievements and skills
- Contact info (email, phone, location, linkedin) on lines right after the name`;

  const userPrompt = `Job Description:\n${jobDescription}\n\nCurrent Resume:\n${currentResume}\n\nPlease tailor this resume for the job description.`;

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt }
  ];

  onProgress?.('Generating tailored resume...');
  const response = await callChat(messages, 0.3);
  onProgress?.('Resume generation complete');

  return response.choices[0].message.content;
}

/**
 * Refine paragraphs for better quality (2-pass processing)
 * Matches vanilla JS refinement logic (lines 1304-1365)
 * @param content - Content to refine
 * @param temperature - Temperature for refinement
 * @param onProgress - Optional callback for progress updates
 * @returns Refined content
 */
export async function refineContent(
  content: string,
  temperature: number = 0.2,
  onProgress?: (message: string) => void
): Promise<string> {
  onProgress?.('Refining content for clarity and impact...');

  const messages: ChatMessage[] = [
    {
      role: 'system',
      content: 'You are an expert editor. Improve the following text for clarity, impact, and professionalism while maintaining its core message.'
    },
    {
      role: 'user',
      content: content
    }
  ];

  const response = await callChat(messages, temperature);
  onProgress?.('Refinement complete');

  return response.choices[0].message.content;
}

/**
 * Split text into paragraphs (helper for refinement)
 * Matches vanilla JS logic
 */
function splitIntoParagraphs(text: string): string[] {
  return text.split(/\n\s*\n/).map(p => p.trim()).filter(p => p.length > 0);
}

/**
 * Join paragraphs back into text
 */
function joinParagraphs(paragraphs: string[]): string {
  return paragraphs.join('\n\n');
}

/**
 * Check if paragraph is a section header
 * Matches vanilla JS logic
 */
function appearsToBeSection(p: string): boolean {
  return /^(SUMMARY|EXPERIENCE|EDUCATION|SKILLS|PROJECTS|CERTIFICATIONS)/i.test(p.trim()) || /^[A-Z\s]+:/.test(p.trim());
}

/**
 * Refine paragraphs with 2-pass processing
 * Matches vanilla JS refinement flow (lines 1317-1365)
 * @param fullResume - Full resume text to refine
 * @param sharedContext - Context string with word banks
 * @param passes - Number of refinement passes (default: 2)
 * @param onProgress - Optional callback for progress updates
 * @returns Refined resume text
 */
export async function refineParagraphs(
  fullResume: string,
  sharedContext: string,
  passes: number = 2,
  onProgress?: (message: string) => void
): Promise<string> {
  let refined = fullResume;
  const paragraphs = splitIntoParagraphs(refined);

  for (let pass = 1; pass <= passes; pass++) {
    onProgress?.(`Refinement pass ${pass}/${passes}...`);

    const refinedParagraphs: string[] = [];
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i];

      // Skip section headers
      if (appearsToBeSection(paragraph)) {
        refinedParagraphs.push(paragraph);
        continue;
      }

      // Skip very short paragraphs
      if (paragraph.length < 50) {
        refinedParagraphs.push(paragraph);
        continue;
      }

      // Refine this paragraph
      const refineMessages: ChatMessage[] = [
        {
          role: 'system',
          content: `You are a professional resume writer refining resume content. ${sharedContext}`
        },
        {
          role: 'user',
          content: `Refine this resume section for clarity, impact, and professional presentation. Keep the same meaning but improve readability and professional appeal:\n\n${paragraph}`
        }
      ];

      try {
        const refineResult = await callChat(refineMessages, 0.2);
        if (refineResult.choices && refineResult.choices[0]) {
          refinedParagraphs.push(refineResult.choices[0].message.content.trim());
        } else {
          refinedParagraphs.push(paragraph);
        }
      } catch (error) {
        onProgress?.(`Refinement error on section ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        refinedParagraphs.push(paragraph);
      }

      // Small delay between requests to avoid rate limits
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    refined = joinParagraphs(refinedParagraphs);
  }

  return refined;
}

/**
 * Process PDF text with AI for formatting
 * @param extractedText - Text extracted from PDF
 * @returns Formatted resume data
 */
export async function processResumeFromPDF(extractedText: string): Promise<string> {
  const messages: ChatMessage[] = [
    {
      role: 'system',
      content: 'You are a resume formatting specialist. Convert the extracted PDF text into a clean, well-formatted resume.'
    },
    {
      role: 'user',
      content: `Please format this resume text:\n\n${extractedText}`
    }
  ];

  const response = await callChat(messages, 0.2);
  return response.choices[0].message.content;
}

/**
 * Parse achievements from LLM response with fallback strategies
 * @param content - Raw content from LLM
 * @returns Array of achievement strings
 */
function parseAchievements(content: string): string[] {
  // Try direct JSON parse first
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
      return parsed;
    }
  } catch {
    // Continue to fallback strategies
  }

  // Try to extract JSON array from content (LLM might add explanation text)
  const jsonArrayMatch = content.match(/\[[\s\S]*?\]/);
  if (jsonArrayMatch) {
    try {
      const parsed = JSON.parse(jsonArrayMatch[0]);
      if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
        return parsed;
      }
    } catch {
      // Continue to line parsing fallback
    }
  }

  // Fallback: Parse bullet points or numbered lists
  // Only use this if content has clear bullet/list structure
  const bulletPattern = /^[-•*]\s+.+/;
  const numberedPattern = /^\d+[.)]\s+.+/;

  const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);

  // Check if content looks like a bulleted or numbered list
  const hasBulletStructure = lines.some(line => bulletPattern.test(line) || numberedPattern.test(line));

  if (!hasBulletStructure) {
    // Content doesn't look like a list, return empty to trigger INVALID_RESPONSE
    return [];
  }

  const achievements = lines
    .filter(line => bulletPattern.test(line) || numberedPattern.test(line))
    .map(line => {
      // Remove common bullet/number prefixes
      return line
        .replace(/^[-•*]\s*/, '')
        .replace(/^\d+[.)]\s*/, '')
        .trim();
    })
    .filter(line => line.length > 10); // Filter out very short lines

  return achievements;
}

/**
 * Generate an experience entry using AI from wizard data
 * @param wizardData - User-provided experience data from wizard
 * @param context - Additional context for generation (job description, word banks, etc.)
 * @returns Result with generated entry or error
 */
export async function generateExperienceEntry(
  wizardData: ExperienceWizardData,
  context: AugmentationContext
): Promise<ExperienceEntryResult> {
  // Build word bank context if provided
  let wordBankContext = '';
  if (context.wordBanks) {
    const { actionVerbs, skillsKeywords, achievementPhrases, industryTerms } = context.wordBanks;
    if (actionVerbs && actionVerbs.length > 0) {
      wordBankContext += `\nAction Verbs to use: ${actionVerbs.join(', ')}`;
    }
    if (skillsKeywords && skillsKeywords.length > 0) {
      wordBankContext += `\nSkills to highlight: ${skillsKeywords.join(', ')}`;
    }
    if (achievementPhrases && achievementPhrases.length > 0) {
      wordBankContext += `\nAchievement phrases: ${achievementPhrases.join(', ')}`;
    }
    if (industryTerms && industryTerms.length > 0) {
      wordBankContext += `\nIndustry terms: ${industryTerms.join(', ')}`;
    }
  }

  const systemPrompt = `You are an expert resume writer specializing in creating impactful achievement-focused bullet points.

Your task is to generate 3-5 professional achievement bullets for a work experience entry.

Guidelines:
- Start each bullet with a strong action verb
- Include quantifiable results where possible (percentages, numbers, dollar amounts)
- Focus on impact and outcomes, not just duties
- Keep each bullet concise (1-2 lines)
- Make bullets ATS-friendly with relevant keywords
${wordBankContext}
${context.jobDescription ? `\nTarget job context: ${context.jobDescription}` : ''}
${context.jobField ? `\nIndustry: ${context.jobField}` : ''}

IMPORTANT: Return ONLY a JSON array of strings. No explanation, no markdown, just the array.
Example: ["Led team of 5 engineers to deliver project 2 weeks ahead of schedule", "Reduced costs by 25% through process optimization"]`;

  const userPrompt = `Generate achievement bullets for this work experience:

Job Title: ${wizardData.jobTitle}
Company: ${wizardData.company}
Duration: ${wizardData.startDate} to ${wizardData.endDate}
${wizardData.location ? `Location: ${wizardData.location}` : ''}

Experience Summary:
${wizardData.experienceSummary}

Skills to Highlight:
${wizardData.skillsToHighlight}

Return a JSON array of 3-5 achievement bullet strings.`;

  const messages: ChatMessage[] = [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt }
  ];

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const data = await callChat(messages, 0.4, DEFAULT_MODEL, controller.signal);
    clearTimeout(timeoutId);

    const content = data.choices?.[0]?.message?.content;
    if (!content) {
      return {
        success: false,
        error: {
          code: 'INVALID_RESPONSE',
          message: 'Empty response from AI',
          retryable: true
        }
      };
    }

    const achievements = parseAchievements(content);
    if (achievements.length === 0) {
      return {
        success: false,
        error: {
          code: 'INVALID_RESPONSE',
          message: 'Could not parse achievements from response',
          retryable: true
        }
      };
    }

    return {
      success: true,
      entry: {
        title: wizardData.jobTitle,
        company: wizardData.company,
        startDate: wizardData.startDate,
        endDate: wizardData.endDate,
        location: wizardData.location || '',
        achievements
      }
    };

  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        error: {
          code: 'TIMEOUT',
          message: 'Request timed out. Please try again.',
          retryable: true
        }
      };
    }

    return {
      success: false,
      error: {
        code: 'API_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        retryable: true
      }
    };
  }
}

/**
 * Wrapper function for generateTailoredResume to match BuilderPage interface
 * @param params - Resume generation parameters
 * @returns Generated resume text
 */
export async function generateResume(params: {
  currentResume: string;
  jobDescription: string;
  wordBanks: WordBanks;
  resumeGuidelines: string[];
  profilePicture: string | null;
  enableRefinement: boolean;
}): Promise<string> {
  const { currentResume, jobDescription, wordBanks, enableRefinement } = params;

  let result = await generateTailoredResume(jobDescription, currentResume, wordBanks);

  if (enableRefinement) {
    result = await refineContent(result, 0.2);
  }

  return result;
}

// Per-item rationale shape returned by retailor functions. Surfaced in
// the UI via a "Why?" toggle next to Accept/Revert so users can see what
// the model was trying to do (which JD requirement / keyword it was
// pulling toward) without reading the diff blind.
export interface RetailorItemRationale {
  text: string;             // The rewritten item content
  rationale: string;        // 1-sentence justification, JD-citation-shaped
  keywords: string[];       // JD-sourced keywords actually present in `text`
}

export interface RetailorSectionResult {
  items: RetailorItemRationale[];
}

/**
 * Retailor a single resume section using AI. Returns one rationale per
 * rewritten item so the UI can show a "Why?" toggle.
 */
export async function retailorSection(
  sectionText: string,
  sectionTitle: string,
  jobDescription: string,
  wordBanks?: Partial<WordBanks>,
  otherSectionTitles?: string[]
): Promise<RetailorSectionResult> {
  let context = '';
  if (wordBanks) {
    if (wordBanks.actionVerbs?.length) context += `Action Verbs: ${wordBanks.actionVerbs.join(', ')}\n`;
    if (wordBanks.skillsKeywords?.length) context += `Skills: ${wordBanks.skillsKeywords.join(', ')}\n`;
    if (wordBanks.achievementPhrases?.length) context += `Achievement Phrases: ${wordBanks.achievementPhrases.join(', ')}\n`;
    if (wordBanks.industryTerms?.length) context += `Industry Terms: ${wordBanks.industryTerms.join(', ')}\n`;
  }

  const otherSections = otherSectionTitles?.length
    ? `\nThe full resume also contains these sections: ${otherSectionTitles.join(', ')}`
    : '';

  const messages: ChatMessage[] = [
    {
      role: 'system',
      content: `You are an expert resume writer. Rewrite the items in the ${sectionTitle} section to better align with the target job description. Preserve all factual information. Make it ATS-friendly.

Return JSON ONLY (no markdown fences, no commentary), with this shape:
{
  "items": [
    {
      "text": "the rewritten item - bullet, job title line, skill, etc.",
      "rationale": "1-sentence WHY for this rewrite. MUST cite a specific requirement or keyword from the job description. Bad: 'Sounds more professional.' Good: 'Surfaces \\'distributed systems\\' from the job description\\'s required-skills list.'",
      "keywords": ["keywords", "from", "the", "job", "description", "actually", "present", "in", "text"]
    }
  ]
}

Rules:
- Output the SAME number of items as the source, in the SAME order.
- Don't fabricate experience. If you can't improve an item, return it unchanged with rationale: "No change needed".
- "rationale" must reference the job description specifically; never say "improves clarity" or "more professional".
- Use bullet glyphs / formatting in "text" only if the source had them.${context ? `\n\nUse these resources:\n${context}` : ''}${otherSections}`
    },
    {
      role: 'user',
      content: `Job Description:\n${jobDescription}\n\nSection to rewrite (${sectionTitle}):\n${sectionText}\n\nReturn ONLY the JSON object.`
    }
  ];

  const response = await callChat(messages, 0.3);
  const raw = response.choices[0]?.message?.content?.trim() ?? '';
  const stripped = raw.replace(/^```(?:json)?\n?/i, '').replace(/\n?```$/i, '').trim();
  const parsed = JSON.parse(stripped) as { items?: unknown };
  if (!Array.isArray(parsed.items)) {
    throw new Error('AI returned malformed section retailor output (no items array).');
  }
  const items: RetailorItemRationale[] = parsed.items
    .filter((it): it is Record<string, unknown> => !!it && typeof it === 'object')
    .map(it => ({
      text: typeof it.text === 'string' ? it.text : '',
      rationale: typeof it.rationale === 'string' ? it.rationale : '',
      keywords: Array.isArray(it.keywords)
        ? (it.keywords as unknown[]).filter((k): k is string => typeof k === 'string')
        : [],
    }))
    .filter(it => it.text.trim().length > 0);
  if (items.length === 0) {
    throw new Error('AI returned no valid items in section retailor output.');
  }
  return { items };
}

/**
 * Retailor a single bullet point or paragraph inline. Returns rationale +
 * keywords alongside the rewrite so the UI can show a "Why?" toggle.
 */
export async function retailorInline(
  itemText: string,
  sectionTitle: string,
  jobDescription: string,
  wordBanks?: Partial<WordBanks>
): Promise<RetailorItemRationale> {
  let context = '';
  if (wordBanks) {
    if (wordBanks.actionVerbs?.length) context += `Action Verbs: ${wordBanks.actionVerbs.join(', ')}\n`;
    if (wordBanks.skillsKeywords?.length) context += `Skills: ${wordBanks.skillsKeywords.join(', ')}\n`;
  }

  const messages: ChatMessage[] = [
    {
      role: 'system',
      content: `You are an expert resume writer. Rewrite the following text to better match the job description. Keep it concise and impactful.

Return JSON ONLY (no markdown fences, no commentary):
{
  "text": "the rewritten item",
  "rationale": "1-sentence WHY for the rewrite, citing a specific requirement or keyword from the job description. Bad: 'More professional.' Good: 'Quantifies impact to match the job description\\'s emphasis on measurable outcomes.'",
  "keywords": ["keywords", "from", "the", "job", "description", "in", "the", "rewritten", "text"]
}

Rules:
- Don't fabricate experience.
- If the source is already strong, set text to the original and rationale to "No change needed".
- "rationale" must cite the job description specifically.${context ? `\n\n${context}` : ''}`
    },
    {
      role: 'user',
      content: `Job Description:\n${jobDescription}\n\nThis appears in the ${sectionTitle} section.\n\nOriginal text:\n${itemText}\n\nReturn ONLY the JSON object.`
    }
  ];

  const response = await callChat(messages, 0.3);
  const raw = response.choices[0]?.message?.content?.trim() ?? '';
  const stripped = raw.replace(/^```(?:json)?\n?/i, '').replace(/\n?```$/i, '').trim();
  const parsed = JSON.parse(stripped) as { text?: unknown; rationale?: unknown; keywords?: unknown };
  return {
    text: typeof parsed.text === 'string' ? parsed.text : '',
    rationale: typeof parsed.rationale === 'string' ? parsed.rationale : '',
    keywords: Array.isArray(parsed.keywords)
      ? (parsed.keywords as unknown[]).filter((k): k is string => typeof k === 'string')
      : [],
  };
}
